import { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Menubar } from 'primereact/menubar';
import { BlockUI } from 'primereact/blockui';
import { Panel } from 'primereact/panel';
import type { DataTableExpandedRows, DataTableFilterEvent, DataTableFilterMeta, DataTablePageEvent } from 'primereact/datatable';
import { FilterMatchMode } from 'primereact/api';
import type { Check, JobStatus, PotentialVendor } from '../../types';
import type { JobProgress } from '../../hooks';

interface EditableVendor extends PotentialVendor {
  expanded?: boolean;
}

interface VendorSuggestionsTabProps {
  vendors: EditableVendor[];
  checks: Check[];
  loading: boolean;
  savingSuggestionId: string | null;
  error: string | null;
  jobStatus?: JobStatus | null;
  jobProgress?: JobProgress | null;
  isIdentifyJobRunning?: boolean;
  onIdentifyVendors: () => void;
  onAddVendor: () => void;
  onAcceptVendor: (suggestionId: string) => void;
  onRejectVendor: (suggestionId: string) => void;
  onRenameVendor: (suggestionId: string, name: string) => void;
  onRemoveCheck: (suggestionId: string, checkId: string) => void;
  onAcceptAll?: () => void;
  isAcceptingAll?: boolean;
}

export const VendorSuggestionsTab: React.FC<VendorSuggestionsTabProps> = ({
  vendors,
  checks,
  loading,
  savingSuggestionId,
  error,
  jobStatus,
  jobProgress,
  isIdentifyJobRunning,
  onIdentifyVendors,
  onAddVendor,
  onAcceptVendor,
  onRejectVendor,
  onRenameVendor,
  onRemoveCheck,
  onAcceptAll,
  isAcceptingAll,
}) => {
  const checksById = useMemo(() => {
    const map = new Map<string, Check>();
    checks.forEach((c) => map.set(c.id, c));
    return map;
  }, [checks]);

  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | undefined>();
  const tableRef = useRef<DataTable<EditableVendor[]>>(null);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(25);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(vendors.length);
  const [statusFilter, setStatusFilter] = useState<string[]>(['pending']);
  const [filters, setFilters] = useState<DataTableFilterMeta>(() => {
    const saved = localStorage.getItem('vendorSuggestionsFilter');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          vendor_name: {
            value: parsed.vendor_name || null,
            matchMode: FilterMatchMode.CONTAINS
          },
        };
      } catch {
        // If parsing fails, fall back to default
      }
    }
    return {
      vendor_name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    };
  });

  const filteredVendors = useMemo(() => {
    return vendors.filter((v) => statusFilter.includes(v.status));
  }, [vendors, statusFilter]);

  const pendingCount = useMemo(() => {
    return vendors.filter((v) => v.status === 'pending').length;
  }, [vendors]);

  useEffect(() => {
    if (!expandedRows) return;

    const currentVendorIds = new Set(
      vendors.filter(v => v.suggestion_id != null).map(v => String(v.suggestion_id))
    );

    const cleanedRows: DataTableExpandedRows = {};
    let hasChanges = false;

    Object.keys(expandedRows).forEach(id => {
      if (currentVendorIds.has(id)) {
        cleanedRows[id] = true;
      } else {
        hasChanges = true;
      }
    });

    if (hasChanges) {
      setExpandedRows(Object.keys(cleanedRows).length > 0 ? cleanedRows : undefined);
    }
  }, [vendors, expandedRows]);

  useEffect(() => {
    setFilteredRowCount(filteredVendors.length);
  }, [filteredVendors]);

  useEffect(() => {
    const vendorNameFilter = filters.vendor_name;
    const filterValue = vendorNameFilter && 'value' in vendorNameFilter ? vendorNameFilter.value : null;
    localStorage.setItem('vendorSuggestionsFilter', JSON.stringify({
      vendor_name: filterValue
    }));
  }, [filters]);

  const onFilter = (e: DataTableFilterEvent) => {
    const filtered = e.filteredValue as EditableVendor[] | null;
    const newCount = filtered ? filtered.length : filteredVendors.length;
    setFilteredRowCount(newCount);
  };

  const onPage = (e: DataTablePageEvent) => {
    setFirst(e.first);
    setRows(e.rows);
    setTimeout(() => {
      const wrapper = tableRef.current?.getElement()?.querySelector('.p-datatable-wrapper');
      if (wrapper) {
        wrapper.scrollTop = 0;
      }
    }, 0);
  };

  const rowClassName = (vendor: EditableVendor) => ({
    [`status-${vendor.status}`]: true,
  });

  const nameEditor = (options: any) => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={(e) => options.editorCallback(e.target.value)}
      />
    );
  };

  const onCellEditComplete = (e: any) => {
    const { rowData, newValue, field } = e;
    if (field === 'vendor_name' && newValue && newValue.trim() !== '') {
      onRenameVendor(rowData.suggestion_id, newValue);
    }
  };

  const summaryBodyTemplate = (vendor: EditableVendor) => (
    <span className="summary-count">
      {vendor.referenced_checks.length}{' '}
      {vendor.referenced_checks.length === 1 ? 'check' : 'checks'}
    </span>
  );

  const actionsBodyTemplate = (vendor: EditableVendor) => (
    <div className="row-actions">
      <Button
        icon="ms-out ms-out-check"
        link
        tooltip="Accept vendor"
        onClick={() => onAcceptVendor(vendor.suggestion_id)}
        disabled={savingSuggestionId === vendor.suggestion_id}
      />
      <Button
        icon="ms-out ms-out-close"
        link
        tooltip="Reject vendor"
        onClick={() => onRejectVendor(vendor.suggestion_id)}
      />
    </div>
  );

  const formatCurrency = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '-';
    const numeric = typeof value === 'number' ? value : parseFloat(String(value));
    if (isNaN(numeric)) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numeric);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}/${date.getFullYear()}`;
    } catch {
      return dateStr;
    }
  };

  const getVendorChecks = (vendor: EditableVendor) => {
    return vendor.referenced_checks
      .map((id) => checksById.get(id))
      .filter((c): c is Check => Boolean(c));
  };

  const checkRemoveBodyTemplate = (vendor: EditableVendor) => (rowData: Check) => (
    <div className="row-actions">
      <Button
        icon="ms-out ms-out-close"
        link
        tooltip="Remove check"
        onClick={() => onRemoveCheck(vendor.suggestion_id, rowData.id)}
      />
    </div>
  );

  const rowExpansionTemplate = (vendor: EditableVendor) => {
    const vendorChecks = getVendorChecks(vendor);

    return (
      <div className="row-expansion-container">
        {vendorChecks.length > 0 && (
          <Panel header="Checks">
            <DataTable value={vendorChecks} size="small">
              <Column field="account_name" header="Account" style={{ width: '20%' }} />
              <Column field="payee_name" header="Payee" style={{ width: '20%' }} />
              <Column field="memo" header="Memo" style={{ width: '15%' }} />
              <Column field="check_number" header="Check #" style={{ width: '10%' }} />
              <Column
                field="check_date"
                header="Date"
                style={{ width: '10%' }}
                body={(rowData: Check) => formatDate(rowData.check_date)}
              />
              <Column
                field="amount"
                header="Amount"
                style={{ width: '10%' }}
                body={(rowData: Check) => formatCurrency(rowData.amount)}
              />
              <Column body={checkRemoveBodyTemplate(vendor)} style={{ width: '8%' }} />
            </DataTable>
          </Panel>
        )}
        {vendorChecks.length === 0 && (
          <Message severity="info" text="No checks assigned to this vendor." />
        )}
      </div>
    );
  };

  const handleExpandAll = () => {
    const allExpanded: DataTableExpandedRows = {};
    filteredVendors.forEach((v) => {
      if (v.suggestion_id != null) {
        allExpanded[String(v.suggestion_id)] = true;
      }
    });
    setExpandedRows(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedRows(undefined);
  };

  const handleToggleStatus = (status: string) => {
    setStatusFilter((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const allExpanded = useMemo(() => {
    if (!expandedRows) return false;
    const expandedCount = Object.keys(expandedRows).length;
    const vendorsWithIds = filteredVendors.filter(v => v.suggestion_id != null).length;
    return expandedCount === vendorsWithIds && vendorsWithIds > 0;
  }, [expandedRows, filteredVendors]);

  const menubarItems = useMemo(
    () => [
      {
        label: isIdentifyJobRunning ? 'Identifying...' : 'Make Suggestions',
        icon: isIdentifyJobRunning ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-bolt',
        command: onIdentifyVendors,
        disabled: loading || isIdentifyJobRunning,
      },
      {
        label: 'Add Vendor',
        icon: 'ms-out ms-out-add',
        command: onAddVendor,
        disabled: loading,
      },
      {
        label: isAcceptingAll ? 'Accepting...' : `Accept All (${pendingCount})`,
        icon: isAcceptingAll ? 'pi pi-spin pi-spinner' : 'ms-out ms-out-check-circle',
        command: onAcceptAll,
        disabled: loading || pendingCount === 0 || !onAcceptAll || isAcceptingAll,
      },
      {
        label: 'Status',
        icon: 'ms-out ms-out-visibility',
        items: [
          {
            label: statusFilter.includes('pending') ? 'Hide Pending' : 'Show Pending',
            command: () => handleToggleStatus('pending'),
          },
          {
            label: statusFilter.includes('accepted') ? 'Hide Accepted' : 'Show Accepted',
            command: () => handleToggleStatus('accepted'),
          },
          {
            label: statusFilter.includes('rejected') ? 'Hide Rejected' : 'Show Rejected',
            command: () => handleToggleStatus('rejected'),
          },
        ],
      },
      {
        label: allExpanded ? 'Collapse All' : 'Expand All',
        icon: allExpanded ? 'ms-out ms-out-expand-less' : 'ms-out ms-out-expand-more',
        command: allExpanded ? handleCollapseAll : handleExpandAll,
        disabled: loading || filteredVendors.length === 0,
      },
    ],
    [loading, isIdentifyJobRunning, onIdentifyVendors, onAddVendor, onAcceptAll, pendingCount, allExpanded, filteredVendors.length, statusFilter, isAcceptingAll]
  );

  return (
    <>
      {error && <Message severity="error" text={error} />}
      <Menubar
        model={menubarItems}
        end={
          <span className="filter-count">
            <span className="filter-container"><strong>{checks.length}</strong> checks</span>
          </span>
        }
        className="actions-menubar"
      />

      {(loading || jobStatus === 'pending' || jobStatus === 'running') && (
        <div className="upload-progress">
          <ProgressBar
            mode={jobProgress?.total ? 'determinate' : 'indeterminate'}
            value={jobProgress?.total ? Math.round((jobProgress.current / jobProgress.total) * 100) : undefined}
          />
          <span className="upload-message">
            {jobStatus === 'pending' && 'Waiting for other job to complete...'}
            {jobStatus === 'running' && (jobProgress?.message || 'Processing...')}
            {!jobStatus && loading && 'Loading...'}
          </span>
        </div>
      )}

      <BlockUI blocked={isAcceptingAll}>
        <DataTable
          ref={tableRef}
          value={filteredVendors}
          dataKey="suggestion_id"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data as DataTableExpandedRows)}
        rowExpansionTemplate={rowExpansionTemplate}
        rowClassName={rowClassName}
        size="small"
        scrollable
        scrollHeight="calc(100vh - 200px)"
        className="vendor-suggestions-table"
        paginator
          first={first}
          rows={rows}
          emptyMessage="No vendor suggestions found"
          rowsPerPageOptions={[25, 50, 100]}
          filterDisplay="menu"
          filters={filters}
          onFilter={(e) => {
            setFilters(e.filters);
            onFilter(e);
          }}
          onPage={onPage}
          totalRecords={filteredRowCount}
        editMode="cell"
        paginatorTemplate={{
          layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
          CurrentPageReport: (options) => (
            <span className="paginator-current">
              Showing {options.first}-{options.last} of <b>{options.totalRecords}</b> vendors
            </span>
          )
        }}
      >
        <Column expander style={{ width: '3%' }} />
        <Column
          field="vendor_name"
          header="Vendor"
          style={{ width: '40%' }}
          editor={nameEditor}
          onCellEditComplete={onCellEditComplete}
          filter
          sortable
          filterPlaceholder="Search by vendor name"
          showFilterMatchModes={false}
        />
        <Column header="Summary" style={{ width: '30%' }} body={summaryBodyTemplate} />
          <Column body={actionsBodyTemplate} style={{ width: '15%' }} />
        </DataTable>
      </BlockUI>
    </>
  );
};

export default VendorSuggestionsTab;
